import {
  Directive,
  DestroyRef,
  EmbeddedViewRef,
  Input,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from '../../core/services/authentication.service';

export type HasPermissionMode = 'remove' | 'disable';

@Directive({
  selector: '[adfHasPermission]',
})
export class HasPermissionDirective implements OnInit {
  private readonly _templateRef = inject(TemplateRef<unknown>);
  private readonly _viewContainer = inject(ViewContainerRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _authService = inject(AuthenticationService);
  private readonly _destroyRef = inject(DestroyRef);

  private _permissionName: string | null = null;
  private _legalEntityId?: number;
  private _divisionId?: number;
  private _productGroupId?: number;
  private _mode: HasPermissionMode = 'remove';

  private _viewRef: EmbeddedViewRef<unknown> | null = null;

  @Input()
  set adfHasPermission(permissionName: string) {
    this._permissionName = permissionName;
    this._updateView();
  }

  @Input()
  set adfHasPermissionLegalEntityId(legalEntityId: number | undefined) {
    this._legalEntityId = legalEntityId;
    this._updateView();
  }

  @Input()
  set adfHasPermissionDivisionId(divisionId: number | undefined) {
    this._divisionId = divisionId;
    this._updateView();
  }

  @Input()
  set adfHasPermissionProductGroupId(productGroupId: number | undefined) {
    this._productGroupId = productGroupId;
    this._updateView();
  }

  @Input()
  set adfHasPermissionMode(mode: HasPermissionMode) {
    this._mode = mode ?? 'remove';
    this._updateView();
  }

  ngOnInit(): void {
    this._authService.appUser$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._updateView();
    });
  }

  private _updateView(): void {
    const hasPermission =
      !!this._permissionName &&
      this._authService.hasUserCalculationPermission(
        this._permissionName,
        this._legalEntityId,
        this._divisionId,
        this._productGroupId
      );

    if (this._mode === 'disable') {
      this._applyDisableMode(hasPermission);
    } else {
      this._applyRemoveMode(hasPermission);
    }
  }

  private _applyRemoveMode(hasPermission: boolean): void {
    if (hasPermission && !this._viewRef) {
      this._viewRef = this._viewContainer.createEmbeddedView(this._templateRef);
    } else if (!hasPermission && this._viewRef) {
      this._viewContainer.clear();
      this._viewRef = null;
    }
  }

  private _applyDisableMode(hasPermission: boolean): void {
    if (!this._viewRef) {
      this._viewRef = this._viewContainer.createEmbeddedView(this._templateRef);
    }

    for (const node of this._viewRef.rootNodes as Node[]) {
      if (!(node instanceof HTMLElement)) {
        continue;
      }

      this._renderer.setProperty(node, 'disabled', !hasPermission);
      this._renderer.setAttribute(node, 'aria-disabled', String(!hasPermission));
    }
  }
}
